import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

// The month a person is working in, for the whole Warning Dashboard.
//
// Every page - the landing cards, each metric's results, each threshold screen - used to start from "this month"
// unless the link that brought you there happened to carry ?month=. Now the month is remembered for ONE HOUR, in
// localStorage (not sessionStorage: that is per tab, so a second tab, a new window or a reopened tab lost it), and the
// page uses, in order,
//   1. ?month= in the address (a shared link, the browser's Back button, a card click, the links between screens),
//   2. the month last used anywhere on the dashboard, if that was within the last hour,
//   3. the current month.
// The hour is sliding: every dashboard page that is opened or changes month renews it, so it only runs out after an
// hour of not touching the dashboard. Picking a month anywhere updates the address and the remembered month.
const STORAGE_KEY = 'warningDashboard.month';
export const REMEMBER_MS = 60 * 60 * 1000;

const read = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (!stored || typeof stored.month !== 'string' || !dayjs(stored.month).isValid()) return null;
    if (Date.now() - Number(stored.at) > REMEMBER_MS) {
      window.localStorage.removeItem(STORAGE_KEY); // an hour has passed - back to the current month
      return null;
    }
    return stored.month;
  } catch (e) {
    return null; // storage blocked or unreadable - fall back to the address / the current month
  }
};

const write = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ month: value, at: Date.now() }));
  } catch (e) {
    /* nothing to do - the month still travels in the address */
  }
};

export const rememberedMonth = () => {
  const value = read();
  return value ? dayjs(value) : null;
};

const useWarningMonth = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlMonth = searchParams.get('month');

  const month = useMemo(() => {
    const raw = urlMonth && dayjs(urlMonth).isValid() ? urlMonth : read();
    return raw ? dayjs(raw) : dayjs();
  }, [urlMonth]);

  // Remember the month this page is showing (and renew the hour): a month that arrived in the address, or the one
  // carried over from the previous screen, is what the next screen reached by a plain link continues from.
  const shown = month.format('YYYY-MM-DD');
  useEffect(() => {
    write(shown);
  }, [shown]);

  const setMonth = useCallback(
    (value) => {
      const formatted = value.format('YYYY-MM-DD');
      write(formatted);
      // replace: stepping through months must not fill the Back history; other params (stage, type, attention)
      // are kept. (This router's setSearchParams takes a plain value, not an updater function.)
      const next = new URLSearchParams(searchParams);
      next.set('month', formatted);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return [month, setMonth];
};

export default useWarningMonth;
